import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../../../styles/panel/user/NewTicket.css";
import { createTicket } from "../../../api/support";
import { fetchMyPendingStores, fetchMyStores } from "../../../api/storeRequest";
import { useAuth } from "../../../context/AuthContext";
import { swalError, swalSuccess } from "../../../utils/swalAlert";
const MAX_IMAGES = 5;
const MAX_SIZE_MB = 5;

const STORE_STATUS_META = {
  active: { label: "فعال", className: "status-active" },
  pending: { label: "در انتظار تایید", className: "status-pending" },
  rejected: { label: "رد شده", className: "status-rejected" },
};

export default function NewTicket() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [storeId, setStoreId] = useState("");
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [images, setImages] = useState([]); // { file, url }
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [storeMenuOpen, setStoreMenuOpen] = useState(false);
  const fileInputRef = useRef(null);
  const storeDropdownRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const loadStores = async () => {
      try {
        const [activeRes, pendingRes] = await Promise.all([
          fetchMyStores(),
          fetchMyPendingStores(),
        ]);
        const active = (activeRes.data || []).map((s) => ({
          ...s,
          status: s.status || "active",
        }));

        const pending = (pendingRes.data || [])
          .filter((s) => s.status === "pending" || s.status === "rejected")
          .map((s) => ({
            id: s.id,
            store_id: `pending-${s.id}`,
            store_name: s.store_name,
            status: s.status,
          }));
        if (isMounted) setStores([...active, ...pending]);
      } catch (err) {
        if (isMounted) {
          setErrors((prev) => ({
            ...prev,
            stores: "دریافت لیست فروشگاه‌ها با خطا مواجه شد.",
          }));
        }
      } finally {
        if (isMounted) setStoresLoading(false);
      }
    };

    loadStores();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!storeMenuOpen) return;
    const handleClickOutside = (e) => {
      if (
        storeDropdownRef.current &&
        !storeDropdownRef.current.contains(e.target)
      ) {
        setStoreMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [storeMenuOpen]);

  const selectedStore = stores.find((s) => s.store_id === storeId);

  const handleSelectStore = (id) => {
    setStoreId(id);
    setStoreMenuOpen(false);
    setErrors((prev) => ({ ...prev, storeId: "" }));
  };

  const handleFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    let localError = "";
    const accepted = [];

    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        localError = "فقط فایل‌های تصویری مجاز هستند.";
        continue;
      }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        localError = `حجم هر تصویر باید کمتر از ${MAX_SIZE_MB} مگابایت باشد.`;
        continue;
      }
      accepted.push(file);
    }

    setImages((prev) => {
      const combined = [...prev, ...accepted].slice(0, MAX_IMAGES);
      return combined.map((file) =>
        file instanceof File ? { file, url: URL.createObjectURL(file) } : file,
      );
    });

    setErrors((prev) => ({
      ...prev,
      images:
        images.length + accepted.length > MAX_IMAGES
          ? `حداکثر ${MAX_IMAGES} تصویر قابل پیوست است.`
          : localError,
    }));

    // امکان انتخاب دوباره‌ی همان فایل را فراهم می‌کند
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed?.url) URL.revokeObjectURL(removed.url);
      return next;
    });
  };

  const validate = () => {
    const next = {};
    if (!title.trim()) next.title = "عنوان تیکت را وارد کنید.";
    // else if (title.trim().length < 5)
    //   next.title = "عنوان باید حداقل ۵ کاراکتر باشد.";

    if (!storeId) next.storeId = "فروشگاه مربوطه را انتخاب کنید.";

    if (!description.trim()) next.description = "توضیحات تیکت را وارد کنید.";
    // else if (description.trim().length < 10)
    //   next.description = "توضیحات باید حداقل ۱۰ کاراکتر باشد.";

    setErrors((prev) => ({ ...prev, ...next }));
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("body", description.trim());
      formData.append("store", storeId);
      images.forEach((img) => formData.append("images", img.file));

      const res = await createTicket(formData);
      const path =
        user?.role === "marketer"
          ? `/marketer/ticket-archive/${res.data.id}`
          : `/panel/user/ticket-archive/${res.data.id}`;

      setTitle("");
      setDescription("");
      setStoreId("");
      images.forEach((img) => img.url && URL.revokeObjectURL(img.url));
      setImages([]);
      setErrors({});

      await swalSuccess({
        title: "success",
        text: "تیکت شما با موفقیت ثبت شد.",
      });

      navigate(path);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        "ارسال تیکت با خطا مواجه شد. لطفاً دوباره تلاش کنید.";

      setErrors((prev) => ({
        ...prev,
        submit: message,
      }));

      swalError({
        title: "خطا در ارسال تیکت",
        text: message || "خطایی در انجام عملیات رخ داد.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ticket-page">
      <div className="ticket-header">
        <h1>ثبت تیکت جدید</h1>
        <p>
          در صورت داشتن سؤال یا مشکل، درخواست خود را برای تیم پشتیبانی ارسال
          کنید.
        </p>
      </div>

      <form className="ticket-form" onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label htmlFor="ticket-title">عنوان تیکت</label>
          <input
            id="ticket-title"
            type="text"
            placeholder="مثلاً: مشکل در پرداخت سفارش"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={errors.title ? "has-error" : ""}
          />
          {errors.title && <span className="field-error">{errors.title}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="ticket-store">فروشگاه</label>
          <div
            className={`custom-select${storeMenuOpen ? " open" : ""}${
              errors.storeId ? " has-error" : ""
            }`}
            ref={storeDropdownRef}
          >
            <button
              type="button"
              id="ticket-store"
              className="custom-select-toggle"
              disabled={storesLoading}
              onClick={() => setStoreMenuOpen((prev) => !prev)}
              aria-haspopup="listbox"
              aria-expanded={storeMenuOpen}
            >
              <span className="custom-select-toggle-content">
                <span className={selectedStore ? "" : "placeholder"}>
                  {storesLoading
                    ? "در حال بارگذاری فروشگاه‌ها..."
                    : selectedStore
                      ? STORE_STATUS_META[selectedStore.status]
                        ? `${selectedStore.store_name} (${
                            STORE_STATUS_META[selectedStore.status].label
                          })`
                        : selectedStore.store_name
                      : "انتخاب فروشگاه"}
                </span>
              </span>
              <svg
                className="custom-select-arrow"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {storeMenuOpen && !storesLoading && (
              <ul className="custom-select-menu" role="listbox">
                <li
                  role="option"
                  aria-selected={storeId === ""}
                  className={`custom-select-option${
                    storeId === "" ? " selected" : ""
                  }`}
                  onClick={() => handleSelectStore("")}
                >
                  انتخاب فروشگاه
                </li>
                {stores.map((store) => {
                  const statusMeta = STORE_STATUS_META[store.status];
                  return (
                    <li
                      key={store.store_id}
                      role="option"
                      aria-selected={storeId === store.store_id}
                      className={`custom-select-option${
                        storeId === store.store_id ? " selected" : ""
                      }`}
                      onClick={() => handleSelectStore(store.store_id)}
                    >
                      {statusMeta
                        ? `${store.store_name} (${statusMeta.label})`
                        : store.store_name}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {errors.storeId && (
            <span className="field-error">{errors.storeId}</span>
          )}
          {errors.stores && (
            <span className="field-error">{errors.stores}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="ticket-description">توضیحات</label>
          <textarea
            id="ticket-description"
            rows={6}
            placeholder="مشکل یا درخواست خود را با جزئیات شرح دهید..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={errors.description ? "has-error" : ""}
          />
          {errors.description && (
            <span className="field-error">{errors.description}</span>
          )}
        </div>

        <div className="form-group">
          <label>تصاویر پیوست (اختیاری)</label>

          <div className="attachments">
            {images.map((img, index) => (
              <div className="attachment-item" key={index}>
                <img src={img.url} alt={`پیوست ${index + 1}`} />
                <button
                  type="button"
                  className="remove-attachment"
                  aria-label="حذف تصویر"
                  onClick={() => removeImage(index)}
                >
                  ×
                </button>
              </div>
            ))}

            {images.length < MAX_IMAGES && (
              <button
                type="button"
                className="attachment-upload"
                onClick={() => fileInputRef.current?.click()}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
                <span>افزودن تصویر</span>
              </button>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={handleFilesChange}
          />

          <span className="field-hint">
            حداکثر {MAX_IMAGES} تصویر، هر کدام تا {MAX_SIZE_MB} مگابایت
          </span>
          {errors.images && (
            <span className="field-error">{errors.images}</span>
          )}
        </div>

        {errors.submit && <div className="form-error">{errors.submit}</div>}

        <div className="form-actions">
          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? "در حال ارسال..." : "ارسال تیکت"}
          </button>
        </div>
      </form>
    </div>
  );
}
